import React, { useMemo } from 'react';
import { Box, Chip, Typography, makeStyles } from '@material-ui/core';
import { ShopMode } from '../redux/types';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  priceWrapper: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
  },
  originalPrice: {
    textDecoration: 'line-through',
    color: theme.palette.text.secondary,
  },
  discountedPrice: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  standardPrice: {
    fontWeight: 500,
  },
  discountBadge: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    height: 20,
    fontSize: '0.7rem',
  },
}));

interface Props {
  price: number;
  discountedPrice?: number;
  shopMode: ShopMode;
  variant?: 'default' | 'large' | 'small';
  showBadge?: boolean;
}

export const PriceDisplay: React.FC<Props> = ({
  price,
  discountedPrice,
  shopMode,
  variant = 'default',
  showBadge = true,
}) => {
  const classes = useStyles();

  const hasDiscount = useMemo(
    () => shopMode === ShopMode.LocalDelivery && discountedPrice && discountedPrice < price,
    [discountedPrice, price, shopMode]
  );

  const discountPercentage = useMemo(
    () => (hasDiscount && discountedPrice ? Math.round(((price - discountedPrice) / price) * 100) : 0),
    [discountedPrice, hasDiscount, price]
  );

  const getFontSize = () => {
    switch (variant) {
      case 'large':
        return { original: 'h5', discounted: 'h4', standard: 'h5' };
      case 'small':
        return { original: 'caption', discounted: 'body2', standard: 'body2' };
      default:
        return { original: 'body2', discounted: 'body1', standard: 'body1' };
    }
  };

  const fontSize = getFontSize();

  return (
    <Box className={classes.container}>
      <Box className={classes.priceWrapper}>
        {hasDiscount ? (
          <>
            <Typography variant={fontSize.original as any} className={classes.originalPrice} component="span">
              €{price.toFixed(2)}
            </Typography>
            <Typography variant={fontSize.discounted as any} className={classes.discountedPrice} component="span">
              €{discountedPrice?.toFixed(2)}
            </Typography>
          </>
        ) : (
          <Typography variant={fontSize.standard as any} className={classes.standardPrice} component="span">
            €{price.toFixed(2)}
          </Typography>
        )}
      </Box>
      {hasDiscount && showBadge && (
        <Chip label={`-${discountPercentage}%`} size="small" className={classes.discountBadge} />
      )}
    </Box>
  );
};
